import { useI18n } from '@ecom/i18n';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useSalesBySourceQuery } from '../api';

import { SOURCE_COLOR, sourceLabel } from '@/features/orders/attribution';
import { formatCatalogPrice } from '@/features/products/format-price';

/**
 * « Ventes par réseau » (migration 0097) — revenue and orders per channel: social apps (from links, ads or the app's
 * in-app browser), search, manual channels. Same counting rule as the revenue figure above (every non-cancelled
 * order once), so the rows add up to it.
 */
export function SalesBySourceCard(): ReactElement | null {
  const { t } = useI18n();
  const query = useSalesBySourceQuery();
  if (query.isPending || query.isError) return null;
  const { entries, totalRevenue } = query.data;

  return (
    <div>
      <h2 className="mb-1 font-display text-lg font-bold text-ink">
        {t('merchant.attribution.title')}
      </h2>
      <p className="mb-3 text-sm text-inkFaint">{t('merchant.attribution.subtitle')}</p>
      {entries.length === 0 ? (
        <EmptyState title={t('merchant.attribution.empty')} />
      ) : (
        <div className="rounded-xl2 border border-line bg-surface p-5 shadow-card">
          <ul className="flex flex-col gap-3">
            {entries.map((entry) => {
              const share = totalRevenue > 0 ? entry.revenue / totalRevenue : 0;
              return (
                <li key={entry.source} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium text-ink">{sourceLabel(entry.source, t)}</span>
                    <span className="font-mono tabular-nums text-inkSoft">
                      {formatCatalogPrice(entry.revenue)} ·{' '}
                      {t('merchant.attribution.orders', {
                        count: entry.orderCount,
                        plural: entry.orderCount > 1 ? 's' : '',
                      })}{' '}
                      · {Math.round(share * 100)} %
                    </span>
                  </div>
                  <div
                    className="h-2 overflow-hidden rounded-full bg-ground"
                    role="img"
                    aria-label={`${sourceLabel(entry.source, t)} ${String(Math.round(share * 100))} %`}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${String(Math.max(share * 100, 2))}%`,
                        background: SOURCE_COLOR[entry.source] ?? 'var(--ecom-accent)',
                      }}
                    />
                  </div>
                  <span className="text-xs text-inkFaint">
                    {t('merchant.attribution.average', {
                      amount: formatCatalogPrice(entry.averageOrderValue),
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-xs text-inkFaint">{t('merchant.attribution.howItWorks')}</p>
        </div>
      )}
    </div>
  );
}
